import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useState } from 'react';
import clsx from 'clsx';
import Tooltip from '@mui/material/Tooltip';
import { forbidden, sso, useNavigate, useQuery } from '../../routes';
import ProductIcon from '../home/ProductIcon';
import { ProductItem } from './MyProductInfo';
import {
  isStudioFeature,
  isRecorderFeature,
  isTestCloudFeature,
  isTestOpsFeature,
  OrganizationFeature,
  TestOpsSubscriptionSource,
  isTestOpsPlatformFeature,
  AccountUser,
  AccountRole,
} from '../../models';
import { BrowserAgent, getBrowserAgent } from './utils';
import { useConfig } from '../../config';
import { licenseUtilization } from '../../layout/routes';
import { UNLIMITED_TEST_EXECUTION } from '../account/utils';
import { fromSubscriptions, fromTestOpsPlatformSubscriptions } from '../../store';
import { useLaunchDarkly } from '../../launchdarkly';

const CHROME_RECORDER_EXTENSION_URL = 'https://chrome.google.com/webstore/detail/katalon-recorder-selenium/ljdobmomdgdljniojadhoplhkpialdid';
const FIREFOX_RECORDER_EXTENSION_URL = 'https://addons.mozilla.org/en-US/firefox/addon/katalon-automation-record/';
const EDGE_RECORDER_EXTENSION_URL = 'https://microsoftedge.microsoft.com/addons/detail/katalon-recorder-seleniu/hdodkejagjkdomgbiioijegfmiiknoam';

const useStyles = makeStyles(theme => ({
  paperRoot: {
    borderRadius: theme.spacing(1),
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingBottom: theme.spacing(2.5),
    minWidth: '32%',
    height: theme.spacing(10.75),
    '&:hover': {
      boxShadow: '0px 6px 8px rgba(209, 209, 209, 0.6)',
      cursor: 'pointer',
    },
  },
  icon: {
    marginTop: theme.spacing(1.75),
  },
  titleProductName: {
    fontSize: theme.spacing(1.75),
    fontWeight: theme.typography.fontWeightMedium,
    minWidth: theme.spacing(33),
    color: '#233145',
  },
  titleRecorder: {
    marginTop: theme.spacing(1.75),
  },
  subTitle: {
    fontSize: theme.spacing(1.5),
    marginTop: theme.spacing(1),
  },
  boxFlex: {
    display: 'flex',
    gap: theme.spacing(1, 2.5),
  },
  boxColumn: {
    display: 'column',
    marginLeft: theme.spacing(1.5),
  },
  header: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(1),
  },
  infoIcon: {
    color: '#808b9a',
    width: theme.spacing(1.75),
    height: theme.spacing(1.75),
    marginLeft: theme.spacing(0.25),
    verticalAlign: 'text-bottom',
  },
  tooltipInfoContent: {
    padding: theme.spacing(0.75),
    maxWidth: '100%',
    fontSize: '0.75rem',
  },
}));

const Bold = styled('span')(({ theme }) => ({
  fontWeight: theme.typography.fontWeightBold,
  fontSize: theme.spacing(1.75),
}));

export interface MyProductsItemProps {
  organizationId: number;
  product: ProductItem;
  domain?: string;
  accountUser?: AccountUser;
}

const MyProductItem = (props: MyProductsItemProps) => {
  const { organizationId, product, domain, accountUser } = props;
  const classes = useStyles();
  const [dialogOpen, setDialogOpen] = useState(false);
  const intl = useIntl();
  const { navigate, replaceQuery } = useNavigate();
  const accountId = Number(useQuery().get('accountId'));
  const { config } = useConfig();
  const { flags } = useLaunchDarkly();
  const orgId = Number(useQuery().get('orgId'));
  const activePlatformSubscription = useSelector(fromTestOpsPlatformSubscriptions
    .selectPaidTestOpsPlatformSubscriptionByAccountId(
      accountId,
    )).filter(sub => new Date(sub.expiryDate).getTime() > Date.now())?.[0];

  const hasTOEnterprise = useSelector(fromSubscriptions
    .selectHasTOEnterpriseByOrganizationId(Number(orgId) || 0));

  const hasEnableSSOAndCustomDomain = (
    activePlatformSubscription?.feature === OrganizationFeature.TESTOPS_PLATFORM
    && activePlatformSubscription.quota >= 30000)
    || hasTOEnterprise;

  const handleClose = () => {
    setDialogOpen(false);
  };
  const { feature } = product;

  const handleProductClick = () => {
    if (isRecorderFeature(feature)) {
      const browserAgent = getBrowserAgent();
      let href = '';
      switch (browserAgent) {
        case BrowserAgent.CHROME_AGENT:
          href = CHROME_RECORDER_EXTENSION_URL;
          break;
        case BrowserAgent.FIREFOX_AGENT:
          href = FIREFOX_RECORDER_EXTENSION_URL;
          break;
        case BrowserAgent.EDGE_AGENT:
          href = EDGE_RECORDER_EXTENSION_URL;
          break;
        default:
          setDialogOpen(true);
          break;
      }
      if (href) {
        const newTabLink = document.createElement('a');
        newTabLink.target = '_blank';
        newTabLink.href = href;
        newTabLink.click();
      }
    } else if (isStudioFeature(feature)) {
      if (accountUser?.role === AccountRole.BILLING_MANAGER && flags?.newNavigationUIEnabled) {
        navigate(forbidden.path, replaceQuery({}));
      } else {
        navigate(licenseUtilization.path, replaceQuery({ orgId: Number(organizationId) }));
      }
    } else {
      const url = domain && hasEnableSSOAndCustomDomain
        ? getSubdomain(domain)
        : config?.testOpsPublicUrl;
      navigate(sso.path, replaceQuery({ redirect_uri: `${url}/organization/${organizationId}/home` }));
    }
  };
  let title = <div />;
  if (isStudioFeature(product.feature)) {
    title = <FormattedMessage id="admin.home.katalon.studio.licenses" values={{ count: product.quota }} />;
  } else if (isTestOpsFeature(product.feature)) {
    title = <FormattedMessage id="admin.home.test.ops.executions" values={{ count: product.quota }} />;
  } else if (isTestCloudFeature(product.feature)) {
    title = <FormattedMessage id="admin.home.test.cloud.sections" values={{ count: product.quota }} />;
  } else if (isTestOpsPlatformFeature(product.feature)) {
    title = <FormattedMessage id="admin.home.test.ops.platform.executions" values={{ count: product.quota }} />;
  }

  const getSubdomain = (domain: string | undefined) => `https://${domain}.${process.env.REACT_APP_CUSTOM_DOMAIN_POSTFIX}`;

  const productName = isTestOpsFeature(product.feature)
    ? OrganizationFeature.getTestOpsFeature(product.feature, product.source)
    : isTestOpsPlatformFeature(product.feature)
      ? OrganizationFeature.getTestOpsPlatformFeature(product.source)
      : OrganizationFeature.getFeatureFullNameWithoutKatalon(product.feature);
  const isUnlimited = product.source === TestOpsSubscriptionSource.TRIAL_REQUEST;

  return (
    <Paper elevation={1} className={classes.paperRoot}>
      <Box display="flex" onClick={handleProductClick}>
        <Box className={classes.icon}>
          <ProductIcon feature={product.feature} />
        </Box>
        <Box className={classes.boxColumn}>
          {!isRecorderFeature(product.feature) && (
            <Typography variant="subtitle2" className={classes.titleProductName}>
              {productName}
            </Typography>
          )}
          {isRecorderFeature(product.feature) && (
            <Typography variant="subtitle2" className={clsx(classes.titleProductName, classes.titleRecorder)}>
              {productName}
            </Typography>
          )}
          {!isTestOpsFeature(product.feature)
            && !isTestOpsPlatformFeature(product.feature) && product?.quota && (
              <Typography variant="subtitle2" className={classes.subTitle}>
                {title}
                &nbsp;
                <Bold>{intl.formatNumber(product.quota)}</Bold>
                {isTestCloudFeature(product.feature)
                  && product.source === TestOpsSubscriptionSource.TRIAL_REQUEST
                  && product.expiryDate && (
                    <Tooltip
                      title={(
                        <div className={classes.tooltipInfoContent}>
                          <FormattedMessage
                            id="admin.home.trial_expiry_date"
                            values={{
                              expiryDate:
                                intl.formatDate(new Date(product.expiryDate), { year: 'numeric', month: 'long', day: 'numeric' }),
                            }}
                          />
                        </div>
                      )}
                      placement="right-end"
                    >
                      <InfoOutlinedIcon fontSize="inherit" className={classes.infoIcon} />
                    </Tooltip>
                )}
              </Typography>
          )}
          {isTestOpsFeature(product.feature) && product?.quota && (
            <Box className={classes.boxFlex}>
              <Typography variant="subtitle2" className={classes.subTitle}>
                {title}
                <Bold>
                  {isUnlimited
                    ? intl.formatMessage({ id: 'home.testops.unlimited' })
                    : intl.formatNumber(product.quota)}
                </Bold>
              </Typography>
              <Typography variant="subtitle2" className={classes.subTitle}>
                <FormattedMessage
                  id="admin.home.test.ops.projects"
                  values={{ count: product.numberProject }}
                />
                <Bold>{product.numberProject}</Bold>
              </Typography>
            </Box>
          )}
          {isTestOpsPlatformFeature(product.feature) && product?.quota && (
            <Box className={classes.boxFlex}>
              <Typography variant="subtitle2" className={classes.subTitle}>
                {title}
                &nbsp;
                <Bold>
                  {product.quota >= UNLIMITED_TEST_EXECUTION
                    ? intl.formatMessage({ id: 'home.testops.unlimited' })
                    : intl.formatNumber(product.quota)}
                </Bold>
                {product.source === TestOpsSubscriptionSource.TRIAL_REQUEST
                && product.quota >= UNLIMITED_TEST_EXECUTION && product.expiryDate && (
                  <Tooltip
                    title={(
                      <div className={classes.tooltipInfoContent}>
                        <FormattedMessage
                          id="admin.home.platform_trial"
                          values={{ expiryDate: intl.formatDate(new Date(product.expiryDate), { year: 'numeric', month: 'long', day: 'numeric' }) }}
                        />
                      </div>
                    )}
                    placement="right-end"
                  >
                    <InfoOutlinedIcon fontSize="inherit" className={classes.infoIcon} />
                  </Tooltip>
                )}
                {product.source === TestOpsSubscriptionSource.TRIAL_REQUEST
                && product.quota < UNLIMITED_TEST_EXECUTION && product.expiryDate && (
                  <Tooltip
                    title={(
                      <div className={classes.tooltipInfoContent}>
                        <FormattedMessage
                          id="admin.home.trial_expiry_date"
                          values={{ expiryDate: intl.formatDate(new Date(product.expiryDate), { year: 'numeric', month: 'long', day: 'numeric' }) }}
                        />
                      </div>
                    )}
                    placement="right-end"
                  >
                    <InfoOutlinedIcon fontSize="inherit" className={classes.infoIcon} />
                  </Tooltip>
                )}
              </Typography>
              <Typography variant="subtitle2" className={classes.subTitle}>
                <FormattedMessage
                  id="admin.home.test.ops.projects"
                  values={{ count: product.numberProject }}
                />
                <Bold>{product.numberProject}</Bold>
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      <Dialog open={dialogOpen} onClose={handleClose}>
        <DialogTitle className={classes.header}>
          <FormattedMessage id="admin.home.popup.title" />
        </DialogTitle>
        <DialogContent>
          <FormattedMessage id="admin.home.popup.content.not.support.browser.agent" />
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default MyProductItem;
